import { FC } from "react";
import { UseFormReturn } from "react-hook-form";
import { Grid2 as Grid, IconButton, MenuItem, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import ReactHookFormDatePicker from "@components/ReactHookFormDatePicker/ReactHookFormDatePicker";
import CustomLabelInput from "@components/CustomLabelInput";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { ImportantDateCategory } from "@models/patient-important-date";
import {
  ImportantDateFormType,
  PatientFormType,
} from "@utils/patientFormHelper";

const ImportantDateForm: FC<{
  index: number;
  form: UseFormReturn<PatientFormType>;
  onDeleteRow: (index: number) => void;
}> = ({
  form: {
    control,
    register,
    formState: { errors },
  },
  index,
  onDeleteRow,
}) => {
  const { t } = useTranslation();
  const categories = Object.values(ImportantDateCategory);
  const rowErrors = errors?.importantDates?.[index] as
    | Partial<Record<keyof ImportantDateFormType, { message?: string }>>
    | undefined;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        <CustomLabelInput
          label={t("personForm.importantDateTitleInput.label")}
          id={`importantDates-${index}-title`}
          required={true}
        >
          <TextField
            {...register(`importantDates.${index}.title`, { required: true })}
            error={!!rowErrors?.title}
            id={`importantDates-${index}-title`}
            type="text"
            size="small"
            variant="outlined"
            fullWidth
            placeholder={t("personForm.importantDateTitleInput.placeholder")}
          />
        </CustomLabelInput>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <CustomLabelInput
          label={t("personForm.importantDateDateInput.label")}
          id={`importantDates-${index}-date`}
          required={true}
        >
          <ReactHookFormDatePicker
            slotProps={{
              textField: {
                size: "small",
                error: !!rowErrors?.date,
              },
            }}
            id={`importantDates-${index}-date`}
            controller={{
              name: `importantDates.${index}.date`,
              control,
              rules: { required: true },
              defaultValue: null,
            }}
          />
        </CustomLabelInput>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <CustomLabelInput
          label={t("personForm.importantDateCategoryInput.label")}
          id={`importantDates-${index}-category`}
        >
          <TextField
            {...register(`importantDates.${index}.category`)}
            id={`importantDates-${index}-category`}
            select
            size="small"
            variant="outlined"
            fullWidth
            defaultValue={ImportantDateCategory.Other}
          >
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                {t(`personForm.importantDateCategory.${category}`)}
              </MenuItem>
            ))}
          </TextField>
        </CustomLabelInput>
      </Grid>
      <Grid
        size={{ xs: 12, md: 1 }}
        display="flex"
        alignItems="flex-end"
        marginBottom="5px"
      >
        <IconButton
          color="error"
          size="small"
          onClick={() => onDeleteRow(index)}
        >
          <DeleteOutlineOutlinedIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

export default ImportantDateForm;
